import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useI18n } from "@/i18n/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Smile, Meh, Frown } from "lucide-react";
import { hora12 } from "@/lib/dateFormat";

const CARAS = [
  { score: 3, Icon: Smile, key: "live_rate_loved", on: "bg-emerald-500 border-emerald-500 text-white", off: "text-emerald-600 border-emerald-200 hover:bg-emerald-50" },
  { score: 2, Icon: Meh, key: "live_rate_ok", on: "bg-amber-500 border-amber-500 text-white", off: "text-amber-600 border-amber-200 hover:bg-amber-50" },
  { score: 1, Icon: Frown, key: "live_rate_meh", on: "bg-rose-500 border-rose-500 text-white", off: "text-rose-600 border-rose-200 hover:bg-rose-50" },
];

/** Le pregunta al estudiante cómo le fue en la clase que acaba de terminar. */
export const LiveRatingPrompt = () => {
  const { t } = useI18n();
  const [sesion, setSesion] = useState(null);
  const [score, setScore] = useState(null);
  const [comment, setComment] = useState("");
  const [saving, setSaving] = useState(false);
  const [gracias, setGracias] = useState(false);

  const cargar = async () => {
    try {
      const r = await api.get("/live-classes/pending-rating");
      setSesion(r.data[0] || null);
      setScore(null);
      setComment("");
    } catch {
      setSesion(null);
    }
  };

  useEffect(() => { cargar(); }, []);

  const enviar = async (skipped) => {
    setSaving(true);
    try {
      await api.post(`/live-classes/${sesion.id}/rate`, { score, comment, skipped });
      if (!skipped) {
        setGracias(true);
        setTimeout(() => { setGracias(false); cargar(); }, 1800);
      } else {
        await cargar();
      }
    } finally {
      setSaving(false);
    }
  };

  if (gracias) {
    return (
      <div className="mb-5 rounded-2xl border border-emerald-200 bg-emerald-50 px-5 py-4 text-sm font-semibold text-emerald-800"
           data-testid="live-rating-thanks">
        {t("live_rate_thanks")}
      </div>
    );
  }

  if (!sesion) return null;

  return (
    <div className="mb-5 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="live-rating-prompt">
      <div className="text-base md:text-lg font-semibold text-slate-900">{t("live_rate_question")}</div>
      <div className="mt-1 text-sm text-slate-500" data-testid="live-rating-session">
        {sesion.title} · {sesion.date} {hora12(sesion.start_time)}
      </div>

      <div className="mt-4 flex flex-wrap gap-2.5">
        {CARAS.map(({ score: sc, Icon, key, on, off }) => (
          <button
            key={sc}
            type="button"
            data-testid={`live-rate-${sc}`}
            onClick={() => setScore(sc)}
            className={`flex items-center gap-2 rounded-full border px-4 py-2.5 text-sm font-semibold transition-colors ${score === sc ? on : `bg-white ${off}`}`}
          >
            <Icon className="h-5 w-5" /> {t(key)}
          </button>
        ))}
      </div>

      {score && (
        <div className="mt-4 flex flex-col gap-2 sm:flex-row">
          <Input
            data-testid="live-rate-comment"
            value={comment}
            maxLength={200}
            onChange={(e) => setComment(e.target.value)}
            placeholder={t("live_rate_comment_ph")}
          />
          <Button data-testid="live-rate-send" disabled={saving} onClick={() => enviar(false)}>
            {t("live_rate_send")}
          </Button>
        </div>
      )}

      <button
        type="button"
        data-testid="live-rate-later"
        disabled={saving}
        onClick={() => enviar(true)}
        className="mt-3 text-xs text-slate-400 underline-offset-2 hover:text-slate-600 hover:underline"
      >
        {t("live_rate_later")}
      </button>
    </div>
  );
};

export default LiveRatingPrompt;
